import { useRef, useState } from "react";
import { Download, FileSpreadsheet, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const AdminImportCsv = ({ onImported }) => {
  const fileRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState("approved");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const { data } = await api.get("/admin/listings/import-template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "dirbiz-import-template.csv";
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const upload = async (event) => {
    const chosen = event.target.files?.[0];
    if (!chosen) return;
    setBusy(true);
    setResult(null);
    const body = new FormData();
    body.append("file", chosen);
    body.append("status", status);
    try {
      const { data } = await api.post("/admin/listings/import", body, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      if (data.created) toast.success(`${data.created} senarai diimport`);
      else toast.error("Tiada baris yang berjaya diimport");
      onImported?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" data-testid="admin-import-open">
          <FileSpreadsheet className="mr-1.5 h-4 w-4" /> Import CSV
        </Button>
      </DialogTrigger>
      <DialogContent data-testid="admin-import-dialog" className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Import senarai dari CSV</DialogTitle>
          <DialogDescription>
            Lajur wajib: business_name, phone_number, category, state, district. Pilihan:
            street_address, postcode, description, owner_email, search_tags. Maksimum 500 baris.
          </DialogDescription>
        </DialogHeader>

        <Button
          variant="outline"
          size="sm"
          data-testid="admin-import-template-button"
          onClick={downloadTemplate}
          className="w-fit"
        >
          <Download className="mr-1.5 h-4 w-4" /> Muat turun templat CSV
        </Button>

        <div className="mt-2">
          <Label>Status senarai yang diimport</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="admin-import-status-select" className="mt-2 h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="approved">LIVE (approved)</SelectItem>
              <SelectItem value="pending">Pending semakan</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          onChange={upload}
          className="hidden"
          data-testid="admin-import-file-input"
        />
        <Button
          data-testid="admin-import-upload-button"
          disabled={busy}
          onClick={() => fileRef.current?.click()}
          className="mt-2 h-11"
        >
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
          {busy ? "Mengimport…" : "Pilih fail CSV"}
        </Button>

        {result && (
          <div data-testid="admin-import-result" className="mt-4 rounded-lg border border-border bg-slate-50 p-4 text-sm">
            <p className="font-semibold text-slate-900">
              {result.created} berjaya · {result.failed} gagal
            </p>
            {result.errors?.length > 0 && (
              <ul className="mt-3 max-h-48 space-y-1.5 overflow-y-auto text-xs text-red-700">
                {result.errors.map((e) => (
                  <li key={`${e.row}-${e.reason}`}>
                    Baris {e.row}: {e.reason}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
